import moment from 'moment';

import { getUtcOffset } from '../../../utils/common';
import { BTSessionStatus, FilterAndSortType } from '../../../constants';
import { timesheetFilter } from './config';
import UserIcon from '../../svgs/UserIcon';
import TagIcon from '../../svgs/TagIcon';
import { TIMESHEET_OPTIONS } from '../../../pages/constants';

import type {
  IFilterAndSortItem,
  IServiceCode,
  ISessionMinified,
  ITimesheet,
  ITimesheetTransformedData,
} from '../../../utils/types';

type TimesheetFilters = Record<string, IFilterAndSortItem>;

function defineTimesheetFilters(filters: TimesheetFilters) {
  return filters;
}

export const THERAPY_HOURS_LABEL = 'Session';
export const NON_THERAPY_HOURS_LABEL = 'Timesheets';

export const getWeekOneAndTwoDateTime = (selectedPayPeriod: string[]) => {
  if (!selectedPayPeriod?.length) return null;
  const weekOneStart = moment(selectedPayPeriod[0]).startOf('day');
  const weekOneEnd = moment(selectedPayPeriod[0]).add(6, 'd').endOf('day');
  const weekTwoStart = moment(selectedPayPeriod[1]).subtract(6, 'd').startOf('day');
  const weekTwoEnd = moment(selectedPayPeriod[1]).endOf('day');
  return {
    weekOne: [weekOneStart, weekOneEnd],
    weekTwo: [weekTwoStart, weekTwoEnd],
  };
};

export const getFilterConfig = (
  timesheets: ITimesheetTransformedData[],
  isReadOnlyView: boolean,
  showTypeFilter: boolean,
) => {
  const filters = defineTimesheetFilters({
    [timesheetFilter.Client]: {
      type: FilterAndSortType.AutoComplete,
      label: 'Client name',
      options: [],
      dataTestid: 'timesheetClientFilter',
      order: 1,
      LabelIcon: UserIcon,
    },
  });
  if (showTypeFilter) {
    filters[timesheetFilter.Type] = {
      type: FilterAndSortType.Checkbox,
      label: 'Type',
      options: [],
      order: 2,
      LabelIcon: TagIcon,
    };
  }
  if (isReadOnlyView) {
    filters[timesheetFilter.Bt] = {
      type: FilterAndSortType.AutoComplete,
      label: 'Provider',
      options: [],
      dataTestid: 'timesheetBtFilter',
      order: 0,
    };
  }
  const clientOptions: Record<string, string> = {};
  const btOptions: Record<string, string> = {};
  const typeOptions = {
    [THERAPY_HOURS_LABEL]: THERAPY_HOURS_LABEL,
    [NON_THERAPY_HOURS_LABEL]: NON_THERAPY_HOURS_LABEL,
  };
  timesheets.forEach((timesheet) => {
    if (timesheet.client_id) {
      clientOptions[timesheet.client_id] = timesheet.client_name || '';
    }
    if (isReadOnlyView && timesheet.provider_id) {
      btOptions[timesheet.provider_id] = timesheet.provider_name || '';
    }
  });
  filters[timesheetFilter.Client].options = Object.entries(clientOptions)
    .map(([id, label], index) => ({
      id,
      label,
      dataTestId:
        index === 0 ? 'timesheetFirstClientFilter' : `clientName-${label.split(' ').join('')}`,
    }))
    .sort((a: { [key: string]: string }, b: { [key: string]: string }) => {
      const aLabel = a.label.toLowerCase();
      const bLabel = b.label.toLowerCase();
      if (aLabel < bLabel) return -1;
      if (aLabel > bLabel) return 1;
      return 0;
    });
  if (showTypeFilter) {
    filters[timesheetFilter.Type].options = Object.entries(typeOptions).map(([id, label]) => ({
      id,
      label,
      dataTestId:
        label === THERAPY_HOURS_LABEL ? 'timesheetTherapyFilter' : 'timesheetNonTherapyFilter',
    }));
  }
  if (isReadOnlyView) {
    filters[timesheetFilter.Bt].options = Object.entries(btOptions)
      .map(([id, label]) => ({
        id,
        label,
      }))
      .sort((a: { [key: string]: string }, b: { [key: string]: string }) => {
        const aLabel = a.label.toLowerCase();
        const bLabel = b.label.toLowerCase();
        if (aLabel < bLabel) return -1;
        if (aLabel > bLabel) return 1;
        return 0;
      });
  }
  return filters;
};

export const getInitialTimesheetState = (
  serviceCodes: IServiceCode[],
  latestPastPayPeriod?: string[],
) => {
  const period =
    latestPastPayPeriod && latestPastPayPeriod.length > 0
      ? latestPastPayPeriod
      : [new Date(), new Date()];

  const date = moment(period[1]).format('YYYY-MM-DD');
  // HH:mm format is used to ignore extra seconds, which causes time inaccuracy in total time calculations
  const time = moment(period[1]).format('HH:mm');
  const endOfDayTime = moment(period[1]).endOf('day').format('HH:mm');

  const currentDateTime = moment(`${date} ${time}`);
  const endOfDayDateTime = moment(`${date} ${endOfDayTime}`);

  return {
    provider_name: '',
    client_id: '',
    service_code_id: serviceCodes[0]['id'] || '',
    date_start: latestPastPayPeriod?.length
      ? moment(endOfDayDateTime).subtract(2, 'hours')
      : moment(currentDateTime).subtract(2, 'hours'),
    date_end: latestPastPayPeriod?.length ? endOfDayDateTime : currentDateTime,
    label: '',
    utc_offset: getUtcOffset(moment(new Date())),
  };
};

export const nonTherapyDataTransformer = (
  timesheets: Array<ITimesheet> = [],
  selectedPayPeriod: Array<string> = [],
) => {
  return timesheets.map((timesheet) => ({
    ...timesheet,
    provider_name: `${timesheet.provider_name?.first_name} ${timesheet.provider_name?.last_name}`,
    client_name: timesheet.client_name
      ? `${timesheet.client_name?.first_name} ${timesheet.client_name?.last_name}`
      : '--',
    service_code: timesheet.service_code?.label,
    read_only: false,
    pay_period: `${moment(selectedPayPeriod[0]).format('MM/DD/YYYY')} - ${moment(
      selectedPayPeriod[1],
    ).format('MM/DD/YYYY')}`,
  }));
};

export const isClientOptional = (serviceCodes: IServiceCode[], serviceCodeId: number | string) => {
  if (!serviceCodeId) return false;
  const selectedServiceCode = serviceCodes.find((serviceCode) => serviceCode.id === serviceCodeId);
  return (
    TIMESHEET_OPTIONS.find((option) => option.label === selectedServiceCode?.label)
      ?.isClientOptional || false
  );
};

export const therapyDataTransformer = (
  sessions: Array<ISessionMinified> = [],
  selectedPayPeriod: Array<string> = [],
) => {
  const transformedData: ITimesheetTransformedData[] = sessions.map((session) => {
    const data = {
      id: parseInt(session.id || ''),
      provider_name: `${session.session_provider?.first_name} ${session.session_provider?.last_name}`,
      provider_id: session.session_provider?.id,
      client_id: parseInt(BigInt(session.client_id).toString()),
      client_name: `${session.client?.first_name} ${session.client?.last_name}`,
      service_code_id: '',
      service_code: THERAPY_HOURS_LABEL,
      date_start: moment(session.date_start).toISOString(),
      date_end: moment(session.date_start).add(session.expected_duration, 'seconds').toISOString(),
      label: '',
      utc_offset: getUtcOffset(moment(new Date())),
      read_only: true,
      pay_period: `${moment(selectedPayPeriod[0]).format('MM/DD/YYYY')} - ${moment(
        selectedPayPeriod[1],
      ).format('MM/DD/YYYY')}`,
    };
    return data;
  });
  return transformedData;
};

export const filterTimesheetTherapy = (
  sessions: Array<ISessionMinified>,
  isReadOnlyView: boolean,
  userEmail: string,
) => {
  return sessions.filter(
    (session: ISessionMinified) =>
      (session.session_provider?.email === userEmail || isReadOnlyView) &&
      session.status !== BTSessionStatus.Started &&
      session.status !== BTSessionStatus.AwaitingGuardianSignature,
  );
};
